/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useEffect, useState } from "react";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import { AccessType } from "@quintype/components";
import { useSelector } from "react-redux";
import AdyenModal from "../../../adyen-modal";
import { getPath, getTextContrast } from "../../../../utils";
import { getSupportedRecurringPaymentProvider } from "../payment-utils";
import LoaderOverlay from "../../../molecules/loader-overlay";
import { SignInMessage } from "../../../atoms/sign-in-message";
import { useShowLoaderAndSignInMsg } from "../custom-hooks";
import { InvalidLink } from "../molecules/invalid-link";
import { textWithFallback } from "../helpers";
import { NotFoundPage } from "../../not-found";
import "./styles.m.css";

const validSwitchTypes = ["upgrade", "downgrade", "crossgrade"];
const FALLBACK_PAGE_TITLE = "<p>Plan Change Overview</p>";
const FALLBACK_PAGE_DESCRIPTION = "<p>Compare current and new subscription plans at a glance.</p>";
const FALLBACK_CURR_PLAN_LABEL = "<p>CURRENT PLAN</p>";
const FALLBACK_NEW_PLAN_LABEL = "<p>NEW PLAN</p>";
const FALLBACK_AMT_PAYABLE = "<p>Amount Payable ^</p>";
const FALLBACK_PROCEED_BTN = "<p>Proceed</p>";
const FALLBACK_NOTE =
  "<p>^ Based on your current subscription, we have applied a prorated discount. The discount is applicable only for the first billing. From the next billing you’ll be charged the base price of the plan.</p>";

const PlanDetails = ({ planLabel, plan }) => {
  const { title, duration_length, duration_unit, price_currency, price_cents } = plan;
  return (
    <div>
      <div
        data-test-id="plan-change-label"
        styleName="plan-label"
        dangerouslySetInnerHTML={{ __html: planLabel }}
      ></div>
      <div data-test-id="plan-change-name-price" styleName="plan-name-and-price">
        <div>{title}</div>
        <div styleName="plan-currency-and-price">
          <span>{price_currency}</span>
          <span styleName="plan-price">{price_cents / 100}</span>
        </div>
      </div>
      <div data-test-id="plan-change-plan-duration" styleName="plan-duration">
        <span>{duration_length}</span>
        <span styleName="plan-duration-unit">{duration_unit}</span>
      </div>
    </div>
  );
};

const openPaymentScreen = async ({ plan, locale, switchType, sketchesHost, whiteListedUrlParams }, opts) => {
  const {
    initRazorPayPayment,
    initPaypalPayment,
    initOmisePayment,
    initPaytrailPayment,
    initAdyenPayment,
    initStripePayment,
    subscriptionId,
  } = opts;

  const modifiedPlan = cloneDeep(plan);
  modifiedPlan["subscriptionId"] = Number(subscriptionId);

  const paymentGateway = modifiedPlan?.recurring
    ? getSupportedRecurringPaymentProvider(modifiedPlan)
    : get(modifiedPlan, ["supported_payment_providers", 0], "razorpay");

  switch (paymentGateway) {
    case "razorpay":
      return initRazorPayPayment(modifiedPlan, "standard", "", "", "", paymentGateway, {
        intent: "switch",
        switchType,
      });
    case "paypal":
      const paypalOptions = {
        selectedPlan: modifiedPlan,
        planType: "standard",
        returnUrl: getPath({sketchesHost, absolutePath: `${document.location.origin}/paypal-payment-success`, injectCustomParams: true, whiteListedUrlParams}),
        cancelUrl: getPath({sketchesHost, absolutePath: `${document.location.origin}/paypal-payment-failed`, injectCustomParams: true, whiteListedUrlParams}),
      };
      return initPaypalPayment(paypalOptions);
    case "omise":
      return initOmisePayment(modifiedPlan, "standard");
    case "paytrail":
      const paytrailOptions = {
        selectedPlan: modifiedPlan,
        planType: "standard",
        returnUrl: getPath({sketchesHost, absolutePath: `${document.location.origin}/paytrail-success.html`, injectCustomParams: true, whiteListedUrlParams}),
        cancelUrl: getPath({sketchesHost, absolutePath: `${document.location.origin}/paytrail-cancel.html`, injectCustomParams: true, whiteListedUrlParams}),
      };

      return initPaytrailPayment(paytrailOptions);
    case "adyen":
      return initAdyenPayment(modifiedPlan, "standard", AdyenModal, locale);
    case "stripe":
      return initStripePayment({
        argType: "options",
        selectedPlan: modifiedPlan,
        planType: "standard",
        successUrl: getPath({sketchesHost, absolutePath: `${document.location.origin}/subscription-success`, injectCustomParams: true, whiteListedUrlParams}),
        cancelUrl: getPath({sketchesHost, absolutePath: `${document.location.origin}/subscription`, injectCustomParams: true, whiteListedUrlParams})
      });
    default:
      console.error(`Payment gateway '${paymentGateway}' is not supported yet`);
  }
};

export const ShowPlanChangeOverview = ({
  getSubscription,
  getSubscriptionsWithSwitchablePlans,
  initAccessType,
  initRazorPayPayment,
  initPaypalPayment,
  initOmisePayment,
  initPaytrailPayment,
  initAdyenPayment,
  initStripePayment,
  enableAccesstype,
  isAtGlobalSet,
  fromPlan,
  toPlan,
  subscriptionId,
  setShowLoader,
  mode,
  switchType,
  redirectUrl,
  target = "web",
}) => {
  const [availablePlans, setAvailablePlans] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  // const [disableProceedBtn, setDisableProceedBtn] = useState(false);
  const [purchasePlanHasFailed, setPurchasePlanHasFailed] = useState(false);
  const [subscriptionsFetched, setSubscriptionsFetched] = useState(false);
  const somethingWentWrongText = useSelector((state) =>
    get(
      state,
      ["qt", "config", "pagebuilder-config", "manage-subscriptions", "pageSetting", "errorMessage"],
      "<p>Something went wrong</p>"
    )
  );
  const config = useSelector((state) => get(state, ["qt", "config"], {}));
  const planChangeOverviewConfig = useSelector((state) =>
    get(state, ["qt", "config", "pagebuilder-config", "plan-change-overview", "rows", "0", "config"], {})
  );
  const locale = useSelector((state) =>
    get(state, ["qt", "config", "pagebuilder-config", "general", "accesstypeIntegration", "locale"], "en-US")
  );
  const subscriptionConfig = useSelector((state) =>
    get(state, ["qt", "config", "pagebuilder-config", "subscription", "pageSetting"], {})
  );
  useEffect(() => {
    try {
      initAccessType(async () => {
        if (enableAccesstype && isAtGlobalSet) {
          const data = await getSubscriptionsWithSwitchablePlans(subscriptionId);
          setSubscriptionsFetched(true);
          const upgradePlanDetails = get(data, ["subscription", "upgrade_plan_details"], []) || [];
          const downgradePlanDetails = get(data, ["subscription", "downgrade_plan_details"], []) || [];
          const crossgradePlanDetails = get(data, ["subscription", "crossgrade_plan_details"], []) || [];
          setAvailablePlans([...upgradePlanDetails, ...crossgradePlanDetails, ...downgradePlanDetails]);

          const subscription = (await getSubscription()) || [];
          const plans = subscription.map((sub) => sub["subscription_plans"]).flat();
          setAllPlans(plans);
          setShowLoader(false);
        }
      });
    } catch (e) {
      console.log(e);
      setShowLoader(false);
    }
  }, [enableAccesstype, isAtGlobalSet]);

  const { showSignInMessage } = useShowLoaderAndSignInMsg();
  if (showSignInMessage) return <SignInMessage />;

  const fromPlanData = allPlans.find((p) => p.id === Number(fromPlan));
  const toPlanData = allPlans.find((p) => p.id === Number(toPlan));
  const additionalToPlanData = availablePlans.find((p) => p.id === Number(toPlan));
  if (!subscriptionsFetched) return null;
  if (subscriptionsFetched && (!fromPlanData || !toPlanData || !additionalToPlanData)) return <InvalidLink />;
  const sketchesHost = get(config, ["sketches-host"], '');
  const whiteListedUrlParams = get(config, ["pagebuilder-config", "general", "others", "whiteListedUrlParams"], [])

  let amountPayable = get(additionalToPlanData, ["prorated_amount_cents"]);
  amountPayable = amountPayable / 100;
  const { pageTitle, pageDescription, currentPlanLabel, newPlanLabel, amountPayableLabel, proceedButton, note } =
    planChangeOverviewConfig;

  const { buttonColor = "#4860BC", darkButtonColor = "#4860BC" } = subscriptionConfig;
  const proceedBtnInlineStyles = {
    background: buttonColor,
    color: getTextContrast(buttonColor) === "dark" ? "#000" : "#fff",
  };
  if (mode === "dark") {
    proceedBtnInlineStyles.background = darkButtonColor;
    proceedBtnInlineStyles.color = getTextContrast(darkButtonColor) === "dark" ? "#000" : "#fff";
  }

  const purchasePlan = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (target !== "web") return;
    // setDisableProceedBtn(true);
    setShowLoader(true);
    try {
      const subscription = await openPaymentScreen(
        { plan: toPlanData, locale, switchType, sketchesHost, whiteListedUrlParams },
        {
          initRazorPayPayment,
          initPaypalPayment,
          initOmisePayment,
          initPaytrailPayment,
          initAdyenPayment,
          initStripePayment,
          subscriptionId,
        }
      );
      if (subscription["subscription"])
        window.location.href = getPath({sketchesHost, absolutePath : `${window?.location?.origin}/subscription-success`, defaultParams: redirectUrl ? `redirectUrl=${redirectUrl}` : "", injectCustomParams: true, whiteListedUrlParams, swapDefaultCustomParams: true});
    } catch (err) {
      console.error(err);
      // setDisableProceedBtn(false);
      setShowLoader(false);
      setPurchasePlanHasFailed(true);
    }
  };

  return (
    <div styleName={`top-lvl-wrapper ${mode}`}>
      {/* {disableProceedBtn && <LoaderOverlay />} */}
      <div styleName="page-content">
        <h1
          data-test-id="plan-change-h1"
          styleName="hidden"
          dangerouslySetInnerHTML={{ __html: textWithFallback(pageTitle, FALLBACK_PAGE_TITLE) }}
        ></h1>
        <div
          data-test-id="plan-change-title"
          styleName="page-title"
          dangerouslySetInnerHTML={{ __html: textWithFallback(pageTitle, FALLBACK_PAGE_TITLE) }}
        ></div>
        <div
          data-test-id="plan-change-description"
          styleName="page-description"
          dangerouslySetInnerHTML={{ __html: textWithFallback(pageDescription, FALLBACK_PAGE_DESCRIPTION) }}
        ></div>
        <div styleName={`plan-details-block ${mode}`}>
          <PlanDetails planLabel={textWithFallback(currentPlanLabel, FALLBACK_CURR_PLAN_LABEL)} plan={fromPlanData} />
          <hr styleName="plan-details-separator" />
          <PlanDetails planLabel={textWithFallback(newPlanLabel, FALLBACK_NEW_PLAN_LABEL)} plan={toPlanData} />
        </div>
        <div styleName={`amount-payable-block ${mode}`}>
          <div
            data-test-id="plan-change-amt-payable-label"
            dangerouslySetInnerHTML={{ __html: textWithFallback(amountPayableLabel, FALLBACK_AMT_PAYABLE) }}
          ></div>
          <div data-test-id="plan-change-amt-payable">
            <span>{toPlanData.price_currency}</span>
            <span styleName="amount-payable">{amountPayable}</span>
          </div>
        </div>
        <button
          styleName="proceed-btn"
          style={proceedBtnInlineStyles}
          dangerouslySetInnerHTML={{ __html: textWithFallback(proceedButton, FALLBACK_PROCEED_BTN) }}
          onClick={purchasePlan}
          // disabled={disableProceedBtn}
          data-test-id="plan-change-proceed-btn"
        />
        {purchasePlanHasFailed && (
          <div
            data-test-id="plan-change-err-msg"
            styleName="err-msg"
            dangerouslySetInnerHTML={{ __html: somethingWentWrongText }}
          />
        )}
        <div
          data-test-id="plan-change-note"
          styleName="note"
          dangerouslySetInnerHTML={{ __html: textWithFallback(note, FALLBACK_NOTE) }}
        />
      </div>
    </div>
  );
};

export const PlanChangeOverview = (props) => {
  const [isAtGlobalSet, setIsAtGlobalSet] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const member = useSelector((state) => get(state, ["member"]));
  const enableDarkMode = useSelector((state) => get(state, ["header", "isDarkModeEnabled"], false));
  const accessTypePbConfig = useSelector((state) =>
    get(state, ["qt", "config", "pagebuilder-config", "general", "accesstypeIntegration"], {})
  );

  const redirectUrl = get(props, ["data", "redirectUrl"]);
  const fromPlan = get(props, ["data", "fromPlan"]);
  const toPlan = get(props, ["data", "toPlan"]);
  const switchType = get(props, ["data", "switchType"]);
  const subscriptionId = get(props, ["data", "subscriptionId"]);
  if (!fromPlan || !toPlan || !subscriptionId) return <InvalidLink />;
  if (!validSwitchTypes.includes(switchType)) return <InvalidLink />;

  const mode = enableDarkMode ? "dark" : "light";
  const { email = "", phone = "" } = member || {};
  const isStaging = get(accessTypePbConfig, ["accesstypeMode"]) === "staging";
  const {
    accesstypeKey = "",
    bridgeKeeperIntegrationId = "",
    enableAccesstype = false,
    accessTypeProdHost = "",
    accessTypeStgHost = "",
  } = accessTypePbConfig;

  if (!enableAccesstype) return <NotFoundPage />;
  const sketchesHost = get(props, ["config", "sketches-host"], '');
  return (
    <>
      {showLoader && <LoaderOverlay />}
      <AccessType
        enableAccesstype={enableAccesstype}
        isStaging={isStaging}
        accessTypeKey={accesstypeKey}
        accessTypeBkIntegrationId={bridgeKeeperIntegrationId}
        email={email}
        phone={phone}
        storyId={""}
        sketchesHost={sketchesHost}
        prodHost={accessTypeProdHost}
        stagingHost={accessTypeStgHost}
        onATGlobalSet={() => {
          setIsAtGlobalSet(true);
        }}
      >
        {(props) => (
          <ShowPlanChangeOverview
            {...props}
            enableAccesstype={enableAccesstype}
            isAtGlobalSet={isAtGlobalSet}
            fromPlan={fromPlan}
            toPlan={toPlan}
            switchType={switchType}
            subscriptionId={subscriptionId}
            setShowLoader={setShowLoader}
            mode={mode}
            redirectUrl={redirectUrl}
          />
        )}
      </AccessType>
    </>
  );
};
